type VisualizationPresentationMode = 'inline' | 'artifact-preferred' | 'artifact-only';

export interface DelegatedToolDisplay {
  toolName: string;
  input?: Record<string, unknown>;
  output?: unknown;
  presentation?: VisualizationPresentationMode;
  title?: string;
}

const CAPABILITY_TOOL_NAMES: Record<string, string> = {
  'media.generate-image': 'generateImage',
  'media.generate-image-batch': 'generateImageBatch',

  'filesystem.list': 'listGalexiaFiles',
  'filesystem.read': 'readGalexiaFile',
  'filesystem.read-lines': 'readGalexiaFileLines',
  'filesystem.search': 'searchGalexiaFiles',
  'filesystem.write-text': 'writeGalexiaTextFile',
  'filesystem.replace-text': 'replaceGalexiaFileText',
  'filesystem.copy-to-workspace': 'copyGalexiaFileToWorkspace',
  'filesystem.prepare-sandbox-files': 'prepareSandboxFiles',
  'filesystem.validate-remotion-source': 'validateRemotionSource',

  'browser.list': 'listBrowsers',
  'browser.create': 'createBrowser',
  'browser.connect': 'connectBrowser',
  'browser.live-view': 'browserLiveView',
  'browser.navigate': 'browserNavigate',
  'browser.scroll': 'browserScroll',
  'browser.playwright': 'browserPlaywright',
  'browser.screenshot': 'browserScreenshot',
  'browser.list-shared-files': 'listSharedFiles',
  'browser.read-shared-file': 'readSharedFile',
  'browser.list-files': 'browserListFiles',
  'browser.read-file': 'browserReadFile',
  'browser.write-file': 'browserWriteFile',
  'browser.export-file': 'exportBrowserFileToSharedStorage',
  'browser.import-file': 'importSharedFileToBrowser',
  'browser.delete': 'deleteBrowser',

  'sandbox.list': 'listSandboxes',
  'sandbox.create': 'createSandbox',
  'sandbox.connect': 'connectSandbox',
  'sandbox.exec': 'sandboxExec',
  'sandbox.code-run': 'sandboxCodeRun',
  'sandbox.session-create': 'sandboxSessionCreate',
  'sandbox.session-list': 'sandboxSessionList',
  'sandbox.session-exec': 'sandboxSessionExec',
  'sandbox.session-logs': 'sandboxSessionLogs',
  'sandbox.session-delete': 'sandboxSessionDelete',
  'sandbox.list-files': 'sandboxListFiles',
  'sandbox.search-files': 'sandboxSearchFiles',
  'sandbox.read-file': 'sandboxReadFile',
  'sandbox.write-file': 'sandboxWriteFile',
  'sandbox.create-folder': 'sandboxCreateFolder',
  'sandbox.delete-path': 'sandboxDeletePath',
  'sandbox.preview-url': 'sandboxPreviewUrl',
  'sandbox.sync': 'sandboxSync',
  'sandbox.restore': 'sandboxRestore',
  'sandbox.list-backups': 'sandboxListBackups',
  'sandbox.export-file': 'exportSandboxFileToSharedStorage',
  'sandbox.import-file': 'importSharedFileToSandbox',
  'sandbox.list-shared-files': 'listSharedFiles',
  'sandbox.read-shared-file': 'readSharedFile',
  'sandbox.stop': 'stopSandbox',
  'sandbox.delete': 'deleteSandbox',
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return asRecord(parsed);
    } catch {
      return undefined;
    }
  }

  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function toolNameFromCapabilityInput(input: Record<string, unknown> | undefined) {
  const nestedInput = asRecord(input?.input);
  const capabilitySlug =
    typeof input?.capabilitySlug === 'string' ? input.capabilitySlug
      : typeof nestedInput?.capabilitySlug === 'string' ? nestedInput.capabilitySlug
        : undefined;
  const actionSlug =
    typeof input?.actionSlug === 'string' ? input.actionSlug
      : typeof nestedInput?.actionSlug === 'string' ? nestedInput.actionSlug
        : undefined;
  if (!capabilitySlug || !actionSlug) return undefined;
  return CAPABILITY_TOOL_NAMES[`${capabilitySlug}.${actionSlug}`];
}

export function resolveDelegatedToolDisplay(
  toolName: string,
  input?: unknown,
  output?: unknown,
): DelegatedToolDisplay {
  const inputRecord = asRecord(input);

  if (toolName !== 'callCapability' && toolName !== 'callTool') {
    return {
      toolName,
      input: inputRecord,
      output,
    };
  }

  const outputRecord = asRecord(output);
  const display = asRecord(outputRecord?.display);
  const callToolName = toolName === 'callTool' && typeof inputRecord?.name === 'string'
    ? inputRecord.name
    : undefined;
  const displayToolName = typeof display?.toolName === 'string'
    ? display.toolName
    : callToolName
      ? CAPABILITY_TOOL_NAMES[callToolName] ?? callToolName
      : toolNameFromCapabilityInput(inputRecord);

  if (!displayToolName) {
    return {
      toolName,
      input: inputRecord,
      output,
    };
  }

  const delegatedInput = asRecord(display?.input)
    ?? asRecord(inputRecord?.input)
    ?? {};
  const delegatedOutput = 'output' in (display ?? {})
    ? display?.output
    : outputRecord?.data ?? output;
  const outputRecordForTitle = asRecord(delegatedOutput);
  const outputBrowserRecord = asRecord(outputRecordForTitle?.browser);
  const presentation = display?.presentation === 'inline' ||
    display?.presentation === 'artifact-preferred' ||
    display?.presentation === 'artifact-only'
    ? display.presentation
    : undefined;
  const title =
    typeof display?.title === 'string' ? display.title
      : typeof outputRecordForTitle?.title === 'string' ? outputRecordForTitle.title
        : typeof outputBrowserRecord?.name === 'string' ? outputBrowserRecord.name
          : undefined;

  return {
    toolName: displayToolName,
    input: delegatedInput,
    output: delegatedOutput,
    presentation,
    title,
  };
}
